import { Injectable, Injector, inject } from '@angular/core';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { PlatformIChingConsultationExportDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-export-dto';
import { PlatformIChingImportResultDto } from '@data/_generated-api/platform-api/models/platform-i-ching-import-result-dto';
import { PlatformImportIChingConsultationItem } from '@data/_generated-api/platform-api/models/platform-import-i-ching-consultation-item';
import { PlatformIChingService } from '@data/_generated-api/platform-api/services/platform-i-ching.service';
import { downloadText } from '@shared/utils/download-file.utils';
import { safeFileName, uniqueFileName } from '@shared/utils/markdown-export.utils';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { ZipEntry, downloadZip } from '@shared/utils/zip-download.utils';
import { HdContentLanguageService } from '../../../chart/bodygraph/hd-content-language.service';
import { HdExportPlanetsService } from '../../../export/hd-export-planets';
import { MdPlanetRow } from '../../../export/hd-export-planets.markdown';
import { injectIChingTranslate } from '../../consult/iching-consult-translate';
import { IChingHexagram, IChingIndexEntry, IChingMethodContent, IChingSourceId } from '../../core/iching.models';
import { HdIChingContentService } from '../../hd-iching-content.service';
import { buildJournalFile, journalFileToJson } from './iching-journal-file';
import {
  IChingJournalIndexRow,
  buildConsultationMarkdown,
  buildJournalIndexMarkdown,
  consultationFileName,
  hexagramsShort,
} from './iching-journal-markdown';

/** Uitkomst van een export; `count` 0 = er viel niets te exporteren. */
export interface IChingExportOutcome {
  readonly count: number;
  /** De planeetstanden konden niet geladen worden; de export ging door zonder. */
  readonly planetsMissing: boolean;
}

/**
 * Export en import van het Classic I Ching-dagboek (spec 2026-10-21-dagboek-momenten-export-design).
 * Zonder id gaat het om het volledige dagboek, met id om één raadpleging. Teksten volgen de
 * content-taal, zoals de rest van de I Tjing.
 */
@Injectable({ providedIn: 'root' })
export class HdIChingJournalExportService {
  private readonly api = inject(PlatformIChingService);
  private readonly content = inject(HdIChingContentService);
  /**
   * BELANGRIJK: de planeetservice wordt pas bij een Markdown-export opgehaald. Ze trekt
   * HdPlanetInfoService (en de vertaaltabellen) mee, en deze service hangt via de dagboeklijst
   * aan elke I Tjing-pagina; meteen injecteren maakte die zwaarder en brak de specs die de
   * dagboeklijst echt renderen (hd-iching-consult.component.spec).
   */
  private readonly injector = inject(Injector);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly t = injectIChingTranslate();

  /** De bron waaruit de Markdown zijn orakeltekst haalt (voor de uitleg in de exportdialoog). */
  currentSource(): IChingSourceId {
    return this.content.sourceFor(this.contentLang.effectiveLang());
  }

  async exportJson(id?: string): Promise<IChingExportOutcome> {
    const rows = await this.loadRows(id);
    if (rows.length === 0) return { count: 0, planetsMissing: false };
    const now = moment();
    // Eén raadpleging: zelfde leesbare naam als haar .md, met .json.
    const fileName = id && rows.length === 1
      ? consultationFileName(rows[0], this.localStamp(rows[0])).replace(/\.md$/, '.json')
      : safeFileName(`${this.t('humanDesign.iching.export.fileBase')} ${now.format('YYYY-MM-DD')}`, 'json');
    downloadText(journalFileToJson(buildJournalFile(rows, now.toISOString())), fileName, 'application/json');
    return { count: rows.length, planetsMissing: false };
  }

  async exportMarkdown(id?: string): Promise<IChingExportOutcome> {
    const rows = await this.loadRows(id);
    if (rows.length === 0) return { count: 0, planetsMissing: false };

    const lang = this.contentLang.effectiveLang();
    const locale = momentLocaleFor(lang);
    const source = this.content.sourceFor(lang);
    const [hexagrams, method, index, planetRows] = await Promise.all([
      this.loadHexagrams(rows, source),
      this.loadMethod(lang),
      this.loadIndex(source),
      this.loadPlanets(rows),
    ]);
    const now = moment().locale(locale);
    const used = new Set<string>();
    const files: ZipEntry[] = [];
    const indexRows: IChingJournalIndexRow[] = [];

    rows.forEach((row, position) => {
      const at = moment(row.consultedAt ?? row.createdOn).locale(locale);
      const primary = hexagrams.get(row.primaryHexagram ?? 0);
      const changed = row.changedHexagram ? hexagrams.get(row.changedHexagram) : undefined;
      const markdown = buildConsultationMarkdown({
        consultation: row,
        primary,
        changed,
        method,
        index,
        planets: planetRows.rows[position] ?? [],
        dateText: at.format('dddd D MMMM YYYY, HH:mm'),
        dateIso: at.format(),
        revisitedText: row.revisitedOn ? moment(row.revisitedOn).locale(locale).format('D MMMM YYYY') : undefined,
        exportedText: now.format('D MMMM YYYY'),
        exportedIso: now.format(),
        lang,
        t: this.t,
      });
      const fileName = uniqueFileName(consultationFileName(row, this.localStamp(row)), used);
      files.push({ path: fileName, content: markdown });
      indexRows.push({
        fileName,
        dateText: at.format('D MMM YYYY, HH:mm'),
        hexagrams: hexagramsShort(row.primaryHexagram ?? 0, primary, row.changedHexagram, changed),
        question: (row.question ?? '').trim(),
      });
    });

    if (id && files.length === 1) {
      downloadText(files[0].content as string, files[0].path, 'text/markdown');
    } else {
      const indexMarkdown = buildJournalIndexMarkdown([...indexRows].reverse(), {
        title: this.t('humanDesign.iching.export.md.indexTitle'),
        summary: this.t('humanDesign.iching.export.md.indexSummary', { count: rows.length, date: now.format('D MMMM YYYY') }),
        date: this.t('humanDesign.iching.export.md.indexDate'),
        hexagrams: this.t('humanDesign.iching.export.md.indexHexagrams'),
        question: this.t('humanDesign.iching.export.md.indexQuestion'),
        guideTitle: this.t('humanDesign.iching.export.md.guideTitle'),
        guideIntro: this.t('humanDesign.iching.export.md.guideIntro'),
        phases: [
          this.t('humanDesign.iching.consult.phase1'),
          this.t('humanDesign.iching.consult.phase2'),
          this.t('humanDesign.iching.consult.phase3'),
        ],
      }, method);
      files.unshift({ path: uniqueFileName('index.md', used), content: indexMarkdown });
      await downloadZip(files, safeFileName(`${this.t('humanDesign.iching.export.fileBase')} ${now.format('YYYY-MM-DD')}`, 'zip'));
    }
    return { count: rows.length, planetsMissing: planetRows.missing };
  }

  /** Importeert de al ingelezen items in het eigen dagboek. */
  async importItems(items: readonly PlatformImportIChingConsultationItem[]): Promise<PlatformIChingImportResultDto> {
    const result = await firstValueFrom(this.api.postIchingConsultationsActionImport({ body: { items: [...items] } }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'Het dagboek kon niet geïmporteerd worden.');
    return result.value;
  }

  private async loadRows(id?: string): Promise<PlatformIChingConsultationExportDto[]> {
    const result = await firstValueFrom(this.api.getIchingConsultationsMineExport(id ? { id } : {}));
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'Het dagboek kon niet geladen worden.');
    return result.value ?? [];
  }

  /** Elk gebruikt hexagram één keer (de content-service cachet per bestand); een laadfout = zonder tekst. */
  private async loadHexagrams(rows: readonly PlatformIChingConsultationExportDto[], source: IChingSourceId): Promise<Map<number, IChingHexagram>> {
    const numbers = new Set<number>();
    for (const row of rows) {
      if (row.primaryHexagram) numbers.add(row.primaryHexagram);
      if (row.changedHexagram) numbers.add(row.changedHexagram);
    }
    const loaded = await Promise.all(
      [...numbers].map(async number => {
        try {
          return [number, await this.content.loadHexagram(number, source)] as const;
        } catch {
          return undefined;
        }
      }),
    );
    return new Map(loaded.filter((entry): entry is readonly [number, IChingHexagram] => entry !== undefined));
  }

  /** Staptitels, uitleg, trigrammen, lijnposities en begrippen; zonder (laadfout) een kalere export. */
  private async loadMethod(lang: string): Promise<IChingMethodContent | undefined> {
    try {
      return await this.content.loadMethod(lang);
    } catch {
      return undefined;
    }
  }

  /** Titels van alle hexagrammen (voor het kernhexagram); zonder blijft enkel het nummer staan. */
  private async loadIndex(source: IChingSourceId): Promise<IChingIndexEntry[] | undefined> {
    try {
      return await this.content.loadIndex(source);
    } catch {
      return undefined;
    }
  }

  private async loadPlanets(rows: readonly PlatformIChingConsultationExportDto[]): Promise<{ rows: MdPlanetRow[][]; missing: boolean }> {
    try {
      const planets = this.injector.get(HdExportPlanetsService);
      return { rows: await planets.rowsAt(rows.map(row => row.consultedAt ?? row.createdOn ?? '')), missing: false };
    } catch {
      return { rows: [], missing: true };
    }
  }

  private localStamp(row: PlatformIChingConsultationExportDto): string {
    return moment(row.consultedAt ?? row.createdOn).format('YYYY-MM-DD HHmm');
  }
}
